import React, { useState } from "react";
import { Bookmark, BookmarkCheck, Upload, Loader2, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * SavePublishBar — drop-in UI strip that lets a signed-in user SAVE the
 * current AI tool output to their library or PUBLISH it to the public Archive.
 *
 * Props:
 *  - kind: "question_mode" | "url_translator" | "source_triangulator"
 *  - topic: short headline (≤240 chars) — usually first URL or topic name
 *  - getPayload: () => object | null — function returning the full result JSON
 *  - testId: optional custom data-testid prefix
 */
export default function SavePublishBar({ kind, topic, getPayload, testId = "save-publish" }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(null);     // saved artifact id (if any)
  const [published, setPublished] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [busyPub, setBusyPub] = useState(false);

  const ensureSaved = async () => {
    if (saved) return saved;
    const payload = getPayload?.();
    if (!payload) { toast.error("No result yet"); return null; }
    const r = await api.post("/ai-artifacts", {
      kind,
      topic: (topic || "").slice(0, 240) || "Untitled",
      payload,
    });
    setSaved(r.data.id);
    setPublished(!!r.data.is_published);
    return r.data.id;
  };

  const onSave = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }));
      return;
    }
    if (busySave) return;
    setBusySave(true);
    try {
      await ensureSaved();
      toast.success("Saved to your library");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusySave(false);
    }
  };

  const onPublish = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("open-auth", { detail: { mode: "signup" } }));
      return;
    }
    if (busyPub) return;
    setBusyPub(true);
    try {
      const id = await ensureSaved();
      if (!id) return;
      await api.post(`/ai-artifacts/${id}/publish`);
      setPublished(true);
      toast.success("Published to the public Archive");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish");
    } finally {
      setBusyPub(false);
    }
  };

  return (
    <div
      data-testid={`${testId}-bar`}
      className="mt-4 flex items-center gap-2 flex-wrap border-t border-white/10 pt-3"
    >
      <button
        type="button"
        onClick={onSave}
        disabled={busySave}
        data-testid={`${testId}-save-btn`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50 ${
          saved
            ? "bg-[#a78bfa]/10 border-[#a78bfa] text-[#a78bfa]"
            : "border-white/15 text-zinc-300 hover:border-[#a78bfa]/60 hover:text-[#a78bfa]"
        }`}
      >
        {busySave ? <Loader2 size={11} className="animate-spin" /> : (saved ? <BookmarkCheck size={11} /> : <Bookmark size={11} />)}
        {saved ? "Saved" : "Save to Library"}
      </button>
      <button
        type="button"
        onClick={onPublish}
        disabled={busyPub || published}
        data-testid={`${testId}-publish-btn`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors disabled:opacity-50 ${
          published
            ? "bg-[#00ff88]/10 border-[#00ff88] text-[#00ff88]"
            : "border-white/15 text-zinc-300 hover:border-[#00ff88]/60 hover:text-[#00ff88]"
        }`}
      >
        {busyPub ? <Loader2 size={11} className="animate-spin" /> : (published ? <Check size={11} /> : <Upload size={11} />)}
        {published ? "Published" : "Publish to Archive"}
      </button>
      {saved && (
        <Link
          to={`/archive?artifact=${saved}`}
          data-testid={`${testId}-view-link`}
          className="inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.25em] text-zinc-500 hover:text-white"
        >
          <ExternalLink size={10} /> VIEW
        </Link>
      )}
      {!user && (
        <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
          // SIGN IN TO SAVE
        </span>
      )}
    </div>
  );
}
